import {
  Button,
  Calendar,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
} from '@design-system';

import { ArrowRight, CalendarIcon } from 'lucide-react';

import { useState } from 'react';
import { format } from 'date-fns';
import {
  useAddPartnerForm,
  SEX_OPTIONS,
  SMOKING_STATUS_OPTIONS,
  type AddPartnerFormValues,
} from '@/features/add-partner';

interface AddPartnerFormProps {
  onSubmit: (data: AddPartnerFormValues) => void;
  isSubmitting?: boolean;
}

export function AddPartnerForm({ onSubmit, isSubmitting = false }: AddPartnerFormProps) {
  const { form, onSubmit: handleSubmit, isValid } = useAddPartnerForm({ onSubmit });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const sendInviteNow = form.watch('sendInviteNow');

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <fieldset disabled={isSubmitting}>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Jane"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Doe"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="birthDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Date of birth <span className="text-destructive">*</span>
                    </FormLabel>
                    <Popover
                      open={calendarOpen}
                      onOpenChange={setCalendarOpen}
                    >
                      <PopoverTrigger type="button">
                        <FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn('w-full justify-start text-left font-normal', {
                              'text-muted-foreground': !field.value,
                            })}
                          >
                            <CalendarIcon />
                            {field.value
                              ? format(field.value as Date, 'MMM d, yyyy')
                              : 'Pick a date'}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="z-99 w-auto p-0"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          captionLayout="dropdown"
                          selected={field.value as Date | undefined}
                          onSelect={date => {
                            field.onChange(date);
                            setCalendarOpen(false);
                          }}
                          defaultMonth={field.value as Date | undefined}
                          disabled={{ after: new Date() }}
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date(new Date().getFullYear(), 0)}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sex"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Sex <span className="text-destructive">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select sex" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SEX_OPTIONS.map(option => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zip code <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="12345"
                        maxLength={5}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="smokingStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Smoking status <span className="text-destructive">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SMOKING_STATUS_OPTIONS.map(option => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Partner email <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="jane@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sendInviteNow"
              render={({ field }) => (
                <FormLabel
                  htmlFor="send-invite-toggle"
                  className="block w-full"
                >
                  <FormItem className="divide-border flex w-full flex-col gap-0 divide-y overflow-hidden rounded-lg border">
                    <div className="flex items-center justify-between gap-4 p-3">
                      <span className="text-base text-gray-950">Send medical questionnaire</span>
                      <FormControl>
                        <Switch
                          id="send-invite-toggle"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </div>
                    <FormDescription className="bg-muted text-balance p-3">
                      Your partner will receive a medical questionnaire to build a complete profile
                      and more personalized LTC projections.
                    </FormDescription>
                  </FormItem>
                </FormLabel>
              )}
            />
          </div>
        </fieldset>

        <Button
          type="submit"
          className="w-full"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Adding partner...' : 'Add partner'}
          {!isSubmitting && <ArrowRight />}
        </Button>
      </form>
    </Form>
  );
}
